import { useEffect, useRef, useState } from 'react';
import { Crosshair, Hand } from 'lucide-react';
import type { PrecisionProps, SessionReplayData } from './types';
import { clamp, getPrecisionRules, trackingQuality, trackingTarget } from './rules';
import { PrecisionStats } from './PrecisionUI';

export default function PrecisionTracking({ game, mode, tier, onComplete, onFail }: PrecisionProps) {
  const rules = getPrecisionRules(game, mode, tier);
  const area = useRef<HTMLDivElement>(null);
  const target = useRef({ x: 50, y: 50 });
  const pointer = useRef({ x: 50, y: 50 });
  const held = useRef(false);
  const pointerId = useRef<number | null>(null);
  const began = useRef(false);
  const finished = useRef(false);
  const [started, setStarted] = useState(false);
  const [view, setView] = useState({ target: target.current, pointer: pointer.current, elapsed: 0, accuracy: 0, quality: 0, combo: 0, loss: 0, held: false, score: 0 });
  const [trail, setTrail] = useState<{ x: number; y: number; targetX: number; targetY: number }[]>([]);
  const callbacks = useRef({ onComplete, onFail });
  callbacks.current = { onComplete, onFail };
  const samples = useRef<NonNullable<SessionReplayData['trackingReplay']>['samples']>([]);

  useEffect(() => {
    if (!started) return;
    finished.current = false;
    let frame = 0;
    let previous = performance.now();
    let elapsed = 0;
    let stableMs = 0;
    let qualitySum = 0;
    let comboMs = 0;
    let maxComboMs = 0;
    let lostMs = 0;
    let breaks = 0;
    let inBreak = false;
    let lastSample = -100;
    const fullMs = rules.trackingSeconds * 1000;
    const loop = (now: number) => {
      if (finished.current) return;
      // Position depends on elapsed time, not display refresh rate.
      const dt = Math.max(0, now - previous);
      previous = now;
      const step = Math.min(dt, fullMs - elapsed);
      elapsed += step;
      const progress = elapsed / fullMs;
      target.current = trackingTarget(elapsed / 1000, rules.trackingSpeed);
      const radius = rules.trackingRadius * (progress > 0.66 ? 0.85 : progress > 0.33 ? 0.93 : 1);
      const distance = Math.hypot(pointer.current.x - target.current.x, pointer.current.y - target.current.y);
      const quality = trackingQuality(distance, radius, held.current);
      const locked = held.current && distance <= radius;
      if (locked) {
        stableMs += step;
        comboMs += step;
        maxComboMs = Math.max(maxComboMs, comboMs);
        lostMs = 0;
        inBreak = false;
      } else {
        lostMs += step;
        comboMs = 0;
        if (!inBreak && lostMs > 400) { breaks++; inBreak = true; }
      }
      qualitySum += quality * step;
      const accuracy = Math.round(stableMs / Math.max(1, elapsed) * 100);
      const score = Math.round(clamp((stableMs / fullMs) * 80 + (qualitySum / fullMs) * 0.2, 0, 100));
      if (elapsed - lastSample >= 80 || elapsed >= fullMs) {
        lastSample = elapsed;
        const sample = { x: pointer.current.x, y: pointer.current.y, targetX: target.current.x, targetY: target.current.y, quality: Math.round(quality), timestamp: Math.round(elapsed) };
        samples.current.push(sample);
        setTrail(samples.current.slice(-22));
        setView({ target: target.current, pointer: pointer.current, elapsed, accuracy, quality: Math.round(quality), combo: Math.floor(comboMs / 1000), loss: lostMs, held: held.current, score });
      } else {
        setView(v => ({ ...v, target: target.current, pointer: pointer.current }));
      }
      const failed = lostMs >= rules.lossLimitMs;
      if (failed || elapsed >= fullMs) {
        finished.current = true;
        const replay: SessionReplayData = {
          mechanic: 'tracking',
          precision: { version: 1, score, accuracy, responseMs: null, bestResponseMs: null, maxCombo: Math.floor(maxComboMs / 1000), perfectCount: 0, mistakes: breaks, total: samples.current.length, tier: rules.level, trials: [], durationSeconds: elapsed / 1000,
            failureReason: failed ? `连续脱离目标 ${(rules.lossLimitMs / 1000).toFixed(1)} 秒。请按住圆环中心跟随，松开或偏离都会中断锁定。` : undefined },
          trackingReplay: {
            samples: samples.current.filter((_, i) => i % 2 === 0),
            postureHints: [{ phase: '操作提示', hint: '此处仅分析触点与目标的距离，不检测头部姿势或眼球运动', score: accuracy }],
            summary: `锁定覆盖率 ${accuracy}%，最长连续锁定 ${(maxComboMs / 1000).toFixed(1)} 秒，平均贴合度 ${Math.round(qualitySum / Math.max(1, elapsed))}%。`,
          },
        };
        if (failed) callbacks.current.onFail(replay); else callbacks.current.onComplete(score, replay);
        return;
      }
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => { finished.current = true; cancelAnimationFrame(frame); };
  }, [started, rules.trackingSeconds, rules.trackingRadius, rules.trackingSpeed, rules.lossLimitMs, rules.level]);

  const updatePointer = (e: React.PointerEvent) => {
    if (!area.current) return;
    const rect = area.current.getBoundingClientRect();
    pointer.current = { x: clamp((e.clientX - rect.left) / rect.width * 100, 0, 100), y: clamp((e.clientY - rect.top) / rect.height * 100, 0, 100) };
  };
  const release = () => { held.current = false; pointerId.current = null; };
  const radius = rules.trackingRadius * (view.elapsed / (rules.trackingSeconds * 1000) > 0.66 ? 0.85 : view.elapsed / (rules.trackingSeconds * 1000) > 0.33 ? 0.93 : 1);
  return <div className="precision-game">
    <div className="precision-hud"><div className="precision-hud-top"><span><Crosshair size={14} /> 连续锁定 · 精细跟随</span><span className="tabular-nums">{Math.max(0, rules.trackingSeconds - view.elapsed / 1000).toFixed(1)} 秒</span></div><div className="precision-progress"><div style={{ width: `${100 - view.elapsed / (rules.trackingSeconds * 10)}%` }} /></div><div className="precision-hud-bottom"><span>阶段 {view.elapsed / (rules.trackingSeconds * 1000) > 0.66 ? '03 · 精细锁定' : view.elapsed / (rules.trackingSeconds * 1000) > 0.33 ? '02 · 收紧范围' : '01 · 稳定跟随'}</span><span>连续锁定 {view.combo} 秒</span></div></div>
    <div className="precision-rule"><span>本轮指令</span><strong>按住目标，平滑跟随</strong><small>内圈为精准区 · 外圈为有效锁定范围</small></div>
    <div ref={area} className="precision-tracking" aria-label="按住并拖动跟随目标的追踪区域"
      onPointerDown={e => {
        if (!e.isPrimary || e.button !== 0) return;
        e.preventDefault(); updatePointer(e);
        if (!began.current && Math.hypot(pointer.current.x - 50, pointer.current.y - 50) > rules.trackingRadius) return;
        pointerId.current = e.pointerId; held.current = true; e.currentTarget.setPointerCapture(e.pointerId);
        if (!began.current) { began.current = true; setStarted(true); }
      }}
      onPointerMove={e => { if (pointerId.current === e.pointerId) updatePointer(e); }}
      onPointerUp={e => { if (pointerId.current === e.pointerId) release(); }}
      onPointerCancel={release} onLostPointerCapture={release}>
      <svg viewBox="0 0 100 100" className="precision-track-svg" aria-hidden="true">
        <defs><pattern id="precision-dots" width="10" height="10" patternUnits="userSpaceOnUse"><circle cx="5" cy="5" r="0.3" fill="#52645b" /></pattern></defs>
        <rect width="100" height="100" fill="url(#precision-dots)" />
        <polyline points={trail.map(p => `${p.targetX},${p.targetY}`).join(' ')} fill="none" stroke="#8aaa96" strokeWidth="0.5" strokeDasharray="1 1" />
        <polyline points={trail.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke="#e1d0b5" strokeWidth="0.5" />
        <circle cx={view.target.x} cy={view.target.y} r={radius} fill="#a0bda910" stroke="#a0bda960" strokeWidth="0.4" />
        <circle cx={view.target.x} cy={view.target.y} r={radius * 0.4} fill="#a0bda930" stroke="#a0bda9" strokeWidth="0.5" />
        <circle cx={view.target.x} cy={view.target.y} r="1.2" fill="#c1d9c9" />
        {view.held && <circle cx={view.pointer.x} cy={view.pointer.y} r="2" fill="none" stroke="#f5dfbb" strokeWidth="0.6" />}
      </svg>
      {!started && <div className="precision-track-start"><Hand size={18} /><span>按住中心圆环开始</span></div>}
      {started && view.loss > 700 && <div className="precision-loss">已脱离 · {(Math.max(0, rules.lossLimitMs - view.loss) / 1000).toFixed(1)} 秒内回到目标</div>}
    </div>
    <PrecisionStats stats={[{ label: '本局评分', value: view.score }, { label: '锁定覆盖率', value: `${view.accuracy}%` }, { label: '当前贴合度', value: `${view.quality}%` }]} />
    <p className="precision-note">松开和偏离均不计分。连击按连续锁定秒数计算；判定依据为手指／鼠标轨迹，不是眼动或姿势检测。</p>
  </div>;
}
